// migrated 1:1 from @nexato/nx-core-module, #688
import { Component, OnInit, forwardRef, inject, DestroyRef, input, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EditSelectable } from '../edit-select-form-control/edit-select-form-control.component';
import { LinkableService } from '../../services/entity-link/linkable/linkable.service';
import { LinkableFilter } from '../../entities/entityLink/linkableFilter';
import { EntityLinkInput } from '../../entities/entityLink/entityLinkInput';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'nx-single-entity-link-widget-input',
  templateUrl: './single-entity-link-widget-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleEntityLinkWidgetInputComponent),
      multi: true
    }
  ]
})
export class SingleEntityLinkWidgetInputComponent implements OnInit, ControlValueAccessor {

  linkedEntityType = input.required<string>();
  linkType = input.required<string>();
  label = input.required<string>();
  placeholder = input.required<string>();
  role = input<string | undefined>(undefined);

  public options: EditSelectable[] = [];

  selectedControl = new FormControl<string | null>(null);
  form = new FormGroup({
    selected: this.selectedControl
  });

  // Injected services
  private readonly linkableService = inject(LinkableService);
  private readonly destroyRef = inject(DestroyRef);

  private onChange: (value: EntityLinkInput | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Reload linkables when linkedEntityType changes
    effect(() => {
      const entityType = this.linkedEntityType();
      if (entityType) {
        this.loadLinkables("");
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to form changes and emit EntityLinkInput
    this.selectedControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(selectedId => {
        const entityLinkInput = this.convertToEntityLinkInput(selectedId);
        this.onChange(entityLinkInput);
        this.onTouched();
      });
  }

  /**
   * Remote filter callback for dropdown
   */
  onFilter = (filterValue: string) => {
    this.loadLinkables(filterValue);
  };

  /**
   * Load available linkables from backend
   */
  private loadLinkables(filterText: string): void {
    const linkableFilter: LinkableFilter = {
      entityTypes: [this.linkedEntityType()],
      text: filterText
    };

    this.linkableService.getLinkables(linkableFilter)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(linkables => linkables.map(linkable => ({
          id: linkable.id,
          name: linkable.text
        }))),
        catchError(error => {
          console.error('Error loading linkables:', error);
          return of([]);
        })
      )
      .subscribe(options => {
        this.options = options;
      });
  }

  /**
   * Converts selected ID to EntityLinkInput with only inward fields
   */
  private convertToEntityLinkInput(inwardEntityId: string | null): EntityLinkInput | null {
    if (!inwardEntityId) {
      return null;
    }

    const entityLinkInput = new EntityLinkInput();
    entityLinkInput.inwardEntityId = inwardEntityId;
    entityLinkInput.inwardEntityType = this.linkedEntityType();
    entityLinkInput.linkType = this.linkType();

    const roleValue = this.role();
    if (roleValue) {
      entityLinkInput.role = roleValue;
    }

    return entityLinkInput;
  }

  // ControlValueAccessor implementation

  writeValue(value: EntityLinkInput | null): void {
    if (value?.inwardEntityId) {
      this.selectedControl.setValue(value.inwardEntityId, { emitEvent: false });
    } else {
      this.selectedControl.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: EntityLinkInput | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedControl.disable();
    } else {
      this.selectedControl.enable();
    }
  }
}
